import { useMemo, useState } from 'react';
import { ArtistIndexModel, ArtistModel } from 'models';
import {
	Flex,
	Text,
	Card,
	CardHeader,
	Button,
	Badge,
	Icon,
	Stack,
} from '@chakra-ui/react';
import RefreshBadge from 'components/RefreshBadge/RefreshBadge';
import ArtistMetricCard from '../Artist/ArtistMetricCard/ArtistMetricCard';
import IndexChartCore from './IndexChartCore';
import { MarketDateFilter } from 'models/types';
import { RiRefreshLine, RiRestartLine, RiAuctionLine } from 'react-icons/ri';
import { FcClock } from 'react-icons/fc';

interface ArtistIndexChartProps {
	artist: ArtistModel;
	artistIndexData: ArtistIndexModel;
	showLastCalculationDate?: boolean;
	showTitle?: boolean;
	showArtistName?: boolean;
	showFilters?: boolean;
	showMetricCard?: boolean;
	onRefreshClick?: () => void;
	onRegenerateClick?: () => void;
	isRefreshing?: boolean;
	isRegenerating?: boolean;
}

function ArtistIndexChart({ artist, artistIndexData, showLastCalculationDate, showTitle = true, showArtistName = true, showFilters = true, showMetricCard = true, onRefreshClick, onRegenerateClick, isRefreshing = false, isRegenerating = false }: ArtistIndexChartProps) {
	const [selectedRange, setSelectedRange] = useState<MarketDateFilter>(MarketDateFilter.MAX);

	const filteredBackfilled = useMemo(() => {
		return artistIndexData.filterDataByMarketDateFilter(
			selectedRange,
			true
		);
	}, [artistIndexData, selectedRange]);

	const metric = filteredBackfilled.length
		? filteredBackfilled[filteredBackfilled.length - 1].weightedIndex
		: 0;
	const priorMetric = filteredBackfilled.length
		? filteredBackfilled[0].weightedIndex
		: undefined;

	const isUpdating = isRegenerating || isRefreshing;
	const isStale = artistIndexData.recalculate;

	const renderStatusIndicator = () => {
		if (isRegenerating) {
			return <RefreshBadge label={'Regenerating'} />;
		}
		if (isRefreshing) {
			return <RefreshBadge label={'Refreshing'} />;
		}
		if (isStale) {
			return (
				<Badge paddingX={2} paddingY={1} borderRadius="md" colorScheme="orange">
					Needs Recalculation
				</Badge>
			);
		}
		return (
			<Badge paddingX={2} paddingY={1} borderRadius="md" colorScheme="green">
				Up To Date
			</Badge>
		);
	};

	const renderTitle = () => {
		return (
			<Flex direction={{ base: 'column', md: 'row' }} gap={2} alignItems={{ base: 'flex-start', md: 'center' }}>
				<Text textAlign="left" fontSize='md' fontWeight='bold'>
					Edition Index History
				</Text>
				{renderStatusIndicator()}
			</Flex>
		);
	};

	function renderActionButtons() {
		if (!onRefreshClick && !onRegenerateClick) {
			return null;
		}

		return (
			<Flex direction={'row'} gap={2} alignSelf="flex-start">
				{onRefreshClick && (
					<Button
						size="sm"
						variant="outline"
						leftIcon={<Icon as={RiRefreshLine} boxSize={4} />}
						onClick={onRefreshClick}
						isLoading={isRefreshing}
						isDisabled={isUpdating}
					>
						Refresh Index
					</Button>
				)}
				{onRegenerateClick && (
					<Button
						size="sm"
						colorScheme="blue"
						leftIcon={<Icon as={RiRestartLine} boxSize={4} />}
						onClick={onRegenerateClick}
						isLoading={isRegenerating}
						isDisabled={isUpdating}
					>
						Regenerate Index
					</Button>
				)}
			</Flex>
		);
	}

	function renderMetricCard() {
		if (showMetricCard && filteredBackfilled.length) {
			return (
				<ArtistMetricCard artist={artist} metric={metric} priorMetricValue={priorMetric} showBorder={false} showLabel={showArtistName} />
			)
		}
	}

	function renderLastCalculationDate() {
		const lastCalculatedDate = artistIndexData.lastCalculationDate?.format('LL');
		const lastCalculatedRelative = artistIndexData.lastCalculationDate?.fromNow();
		const lastCalculatedLabel = lastCalculatedDate
			? `${lastCalculatedDate}${lastCalculatedRelative ? ` (${lastCalculatedRelative})` : ''}`
			: 'Not available';

		const sortedIndexData = artistIndexData.getDateSortedIndexData();
		const latestIndexDataPoint = sortedIndexData.length ? sortedIndexData[0] : undefined;
		const latestIndexDate = latestIndexDataPoint?.date?.format('LL');

		return (
			<Stack spacing={1} alignItems="flex-start">
				<Flex align="center" gap={2}>
					<Icon as={FcClock} boxSize={4} />
					<Text fontWeight='light' textAlign={'start'} fontSize={'xs'}>
						Last Calculated: {lastCalculatedLabel}
					</Text>
				</Flex>
				{latestIndexDate && (
					<Flex align="center" gap={2}>
						<Icon as={RiAuctionLine} boxSize={4} color="orange.400" />
						<Text fontWeight='light' textAlign={'start'} fontSize={'xs'}>
							Last Sale Date: {latestIndexDate}
						</Text>
					</Flex>
				)}
			</Stack>
		)
	}

	return (
		<Card paddingTop={7} paddingX={4} borderRadius={8} width={'100%'} display={'flex'} flexDirection={'column'}>
			<CardHeader paddingY={0}>
				<Flex direction={{ base: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ base: 'flex-start', md: 'center' }} width='100%' gap={4}>
					<Flex direction='column' alignSelf='flex-start' width='100%' gap={2}>
						{showTitle && renderTitle()}
						{renderMetricCard()}
						{showLastCalculationDate && renderLastCalculationDate()}
					</Flex>
					{renderActionButtons()}
				</Flex>
			</CardHeader>
			<IndexChartCore indexData={artistIndexData.backfilledData} showFilters={showFilters} chartId={artist.id} onFilterChange={(filter) => setSelectedRange(filter)} />
		</Card>
	);
}

export default ArtistIndexChart;
